import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Highlight } from '@tiptap/extension-highlight';
import { Underline as TipUnderline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { Link as TipLink } from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { CharacterCount } from '@tiptap/extension-character-count';
import { docsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Share2, History, Bold, Italic, Underline as UIcon,
  Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Code, Quote,
  Undo, Redo, Highlighter, Link2, Unlink, ImagePlus, Table as TableIcon,
  Minus, CheckSquare, Type, Subscript as SubIcon, Superscript as SupIcon,
  Printer, Download, FileText, LayoutDashboard, Search, Replace, ChevronDown
} from 'lucide-react';

const TEXT_COLORS = ['#000000','#434343','#666666','#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6fa8dc','#8e7cc3','#c27ba0','#ffffff'];
const HIGHLIGHT_COLORS = ['#fff2cc','#d9ead3','#cfe2f3','#ead1dc','#fce5cd','#f4cccc','#d0e0e3','transparent'];

const ToolbarBtn = ({ onClick, active, children, title, disabled }) => (
  <button onClick={onClick} title={title} disabled={disabled}
    className={`p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-30 ${active ? 'bg-muted text-primary' : 'text-foreground/70'}`}>
    {children}
  </button>
);

const ToolbarSep = () => <div className="w-px h-5 bg-border mx-0.5" />;

export default function DocEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState('');
  const saveTimerRef = useRef(null);
  const isRemoteUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Placeholder.configure({ placeholder: 'Start typing your document...' }),
      Highlight.configure({ multicolor: true }),
      TipUnderline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TipLink.configure({ openOnClick: false, autolink: true }),
      Image.configure({ inline: true, allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow, TableCell, TableHeader,
      Subscript, Superscript,
      TaskList, TaskItem.configure({ nested: true }),
      CharacterCount,
    ],
    content: '',
    onUpdate: ({ editor }) => {
      if (isRemoteUpdate.current) return;
      const html = editor.getHTML();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => autoSave(html), 3000);
    },
    editorProps: {
      attributes: { class: 'prose prose-sm sm:prose dark:prose-invert max-w-none focus:outline-none min-h-[60vh] px-8 py-6 md:px-16 md:py-8' }
    }
  });

  const autoSave = useCallback(async (content) => {
    if (!docId) return;
    try { await docsApi.update(docId, { content, title }); } catch {}
  }, [docId, title]);

  useEffect(() => {
    if (!docId) return;
    docsApi.get(docId).then(d => {
      setDoc(d);
      setTitle(d.title);
      if (editor && d.content) {
        isRemoteUpdate.current = true;
        editor.commands.setContent(d.content);
        isRemoteUpdate.current = false;
      }
    }).catch(() => { toast.error('Document not found'); navigate('/docs'); });
  }, [docId, editor, navigate]);

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      await docsApi.update(docId, { content: editor.getHTML(), title });
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleShare = async () => {
    try {
      await docsApi.share(docId, shareEmail);
      toast.success(`Shared with ${shareEmail}`);
      setShareEmail(''); setShowShare(false);
    } catch (e) { toast.error(e.message || 'Share failed'); }
  };

  const loadVersions = async () => {
    try {
      const v = await docsApi.versions(docId);
      setVersions(v);
      setShowVersions(true);
    } catch { toast.error('Failed to load versions'); }
  };

  const setLink = () => {
    if (!linkUrl) { editor?.chain().focus().extendMarkRange('link').unsetLink().run(); }
    else { editor?.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run(); }
    setShowLink(false); setLinkUrl('');
  };

  const addImage = () => {
    if (!imageUrl) return;
    editor?.chain().focus().setImage({ src: imageUrl }).run();
    setShowImage(false); setImageUrl('');
  };

  const insertTable = () => {
    editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const exportHtml = () => {
    if (!editor) return;
    const html = `<!DOCTYPE html><html><head><title>${title}</title><style>body{font-family:sans-serif;max-width:800px;margin:40px auto;padding:0 20px;line-height:1.6}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:8px}th{background:#f5f5f5}</style></head><body><h1>${title}</h1>${editor.getHTML()}</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `${title || 'document'}.html`; a.click();
    toast.success('Exported as HTML');
  };

  const printDoc = () => {
    const content = editor?.getHTML();
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title><style>body{font-family:Georgia,serif;max-width:700px;margin:40px auto;line-height:1.8;color:#222}h1,h2,h3{color:#111}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:8px}@media print{body{margin:0}}</style></head><body><h1>${title}</h1>${content}</body></html>`);
    win.document.close();
    win.print();
  };

  const charCount = editor?.storage.characterCount;

  if (!doc) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="doc-editor">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur shrink-0">
        <div className="px-3 py-1.5 flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/docs')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <FileText className="w-5 h-5 text-blue-500" />
          <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={() => docsApi.update(docId, { title })}
            className="text-base font-semibold border-none shadow-none focus-visible:ring-0 px-1 h-7 w-64" data-testid="doc-title" />
          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/dashboard')}><LayoutDashboard className="w-3.5 h-3.5 mr-1" />Home</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={printDoc}><Printer className="w-3.5 h-3.5 mr-1" />Print</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={exportHtml}><Download className="w-3.5 h-3.5 mr-1" />Export</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowShare(true)} data-testid="share-btn"><Share2 className="w-3.5 h-3.5 mr-1" />Share</Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={loadVersions}><History className="w-3.5 h-3.5 mr-1" />History</Button>
            <Button size="sm" className="h-7" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? '...' : 'Save'}</Button>
          </div>
        </div>

        {/* Toolbar */}
        {editor && (
          <div className="px-3 py-1 flex items-center gap-0.5 border-t border-border overflow-x-auto">
            <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" disabled={!editor.can().undo()}><Undo className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" disabled={!editor.can().redo()}><Redo className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            {/* Heading dropdown */}
            <select value={editor.isActive('heading', { level: 1 }) ? '1' : editor.isActive('heading', { level: 2 }) ? '2' : editor.isActive('heading', { level: 3 }) ? '3' : '0'}
              onChange={e => { const v = parseInt(e.target.value); v === 0 ? editor.chain().focus().setParagraph().run() : editor.chain().focus().toggleHeading({ level: v }).run(); }}
              className="h-7 text-xs bg-background border border-border rounded px-1 w-28" data-testid="heading-select">
              <option value="0">Normal Text</option>
              <option value="1">Heading 1</option>
              <option value="2">Heading 2</option>
              <option value="3">Heading 3</option>
            </select>
            <ToolbarSep />
            <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)"><Bold className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)"><Italic className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)"><UIcon className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} title="Subscript"><SubIcon className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} title="Superscript"><SupIcon className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            {/* Text Color */}
            <div className="relative group">
              <button className="p-1.5 rounded hover:bg-muted flex items-center gap-0.5" title="Text Color">
                <Type className="w-4 h-4" /><ChevronDown className="w-2.5 h-2.5 opacity-50" />
              </button>
              <div className="absolute top-full left-0 mt-1 p-2 bg-card border border-border rounded-lg shadow-xl hidden group-hover:grid grid-cols-4 gap-1 z-50">
                {TEXT_COLORS.map(c => <button key={c} onClick={() => editor.chain().focus().setColor(c).run()} className="w-6 h-6 rounded-full border border-border hover:scale-125 transition-transform" style={{ backgroundColor: c }} />)}
              </div>
            </div>
            {/* Highlight */}
            <div className="relative group">
              <button className="p-1.5 rounded hover:bg-muted" title="Highlight"><Highlighter className="w-4 h-4" /></button>
              <div className="absolute top-full left-0 mt-1 p-2 bg-card border border-border rounded-lg shadow-xl hidden group-hover:grid grid-cols-4 gap-1 z-50">
                {HIGHLIGHT_COLORS.map(c => <button key={c} onClick={() => c === 'transparent' ? editor.chain().focus().unsetHighlight().run() : editor.chain().focus().toggleHighlight({ color: c }).run()} className="w-6 h-6 rounded border border-border hover:scale-125 transition-transform" style={{ backgroundColor: c === 'transparent' ? '#fff' : c }} />)}
              </div>
            </div>
            <ToolbarSep />
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left"><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center"><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right"><AlignRight className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justify"><AlignJustify className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List"><List className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Checklist"><CheckSquare className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><Quote className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block"><Code className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule"><Minus className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            <ToolbarBtn onClick={() => { setLinkUrl(editor.getAttributes('link').href || ''); setShowLink(true); }} active={editor.isActive('link')} title="Insert Link"><Link2 className="w-4 h-4" /></ToolbarBtn>
            {editor.isActive('link') && (
              <ToolbarBtn onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link"><Unlink className="w-4 h-4" /></ToolbarBtn>
            )}
            <ToolbarBtn onClick={() => setShowImage(true)} title="Insert Image"><ImagePlus className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn onClick={insertTable} title="Insert Table"><TableIcon className="w-4 h-4" /></ToolbarBtn>
            <ToolbarSep />
            <ToolbarBtn onClick={() => setShowFind(!showFind)} title="Find"><Search className="w-4 h-4" /></ToolbarBtn>
          </div>
        )}
      </header>

      {/* Find bar */}
      {showFind && (
        <div className="px-4 py-2 border-b border-border bg-amber-500/5 flex items-center gap-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={findText} onChange={e => setFindText(e.target.value)} placeholder="Find in document..."
            className="h-7 text-xs w-64" autoFocus />
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setShowFind(false); setFindText(''); }}>Close</Button>
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 overflow-auto bg-background">
        <div className="max-w-4xl mx-auto bg-card shadow-sm border-x border-border min-h-screen">
          <EditorContent editor={editor} data-testid="editor-content" />
        </div>
      </div>

      {/* Status Bar */}
      <div className="border-t border-border bg-card px-4 py-1 flex items-center justify-between text-[10px] text-muted-foreground shrink-0">
        <span>{charCount?.characters() || 0} characters | {charCount?.words() || 0} words</span>
        <span>Auto-save enabled</span>
      </div>


      {/* Share Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent><DialogHeader><DialogTitle>Share Document</DialogTitle></DialogHeader>
          <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Enter email to share with" onKeyDown={e => e.key === 'Enter' && handleShare()} data-testid="share-email" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShare(false)}>Cancel</Button>
            <Button onClick={handleShare}>Share</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Dialog */}
      <Dialog open={showLink} onOpenChange={setShowLink}>
        <DialogContent><DialogHeader><DialogTitle>Insert Link</DialogTitle></DialogHeader>
          <Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://example.com" onKeyDown={e => e.key === 'Enter' && setLink()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { editor?.chain().focus().unsetLink().run(); setShowLink(false); }}>Remove</Button>
            <Button onClick={setLink}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Dialog */}
      <Dialog open={showImage} onOpenChange={setShowImage}>
        <DialogContent><DialogHeader><DialogTitle>Insert Image</DialogTitle></DialogHeader>
          <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="Paste image URL..." onKeyDown={e => e.key === 'Enter' && addImage()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImage(false)}>Cancel</Button>
            <Button onClick={addImage}>Insert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Version History</DialogTitle></DialogHeader>
          <div className="max-h-64 overflow-auto space-y-2">
            {versions.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No versions yet</p> : versions.map((v, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <div><p className="text-sm font-medium">Version {v.version}</p><p className="text-xs text-muted-foreground">{new Date(v.saved_at).toLocaleString()}</p></div>
                <Button size="sm" variant="outline" onClick={() => { editor.commands.setContent(v.content); setShowVersions(false); toast.success('Restored version'); }}>Restore</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
